'use strict';

class Ajaxinate {
  constructor(config) {
    const settings = config || {};

    const defaults = {
      method: 'scroll',
      container: '#AjaxinateContainer',
      pagination: '#AjaxinatePagination',
      productCard: '.product-card',
      offset: 0,
      loadingText: 'Loading',
      callback: null,
      saveHistory: false,
      scrollHistory: false
    };

    this.settings = Object.assign(defaults, settings);

    this.containerElement = document.querySelector(this.settings.container);
    this.paginationElement = document.querySelector(this.settings.pagination);

    this.addClickListener = this.addClickListener.bind(this);
    this.preventMultipleClicks = this.preventMultipleClicks.bind(this);
    this.checkIfPaginationInView = this.checkIfPaginationInView.bind(this);
    this.addScrollListeners = this.addScrollListeners.bind(this);
    this.addClickListenerProductCard = this.addClickListenerProductCard.bind(this);
    this.addScrollPositionSaver = this.addScrollPositionSaver.bind(this);
    this.loadPreviousContent = this.loadPreviousContent.bind(this);
    this.scrollToSavedPosition = this.scrollToSavedPosition.bind(this);
    this.loadMore = this.loadMore.bind(this);
    this.removeClickListener = this.removeClickListener.bind(this);
    this.removePaginationElement = this.removePaginationElement.bind(this);
    this.removeScrollListener = this.removeScrollListener.bind(this);
    this.destroy = this.destroy.bind(this);

    this.isLoaded = false;

    this.initialize();
  }

  initialize() {
    if (!this.containerElement) { return; }

    // Stop the browser restoring its own scroll position on back-nav.
    // We handle it precisely in scrollToSavedPosition.
    if (this.settings.saveHistory) {
      history.scrollRestoration = 'manual';
    }

    const initializers = {
      click: this.addClickListener.bind(this),
      scroll: this.addScrollListeners.bind(this),
    };

    initializers[this.settings.method]();

    if (this.settings.saveHistory) {
      this.addClickListenerProductCard();
      this.addScrollPositionSaver();
    }

    if (this.settings.saveHistory && !this.isLoaded) {
      this.loadPreviousContent();
      this.isLoaded = true;
    }

    if (!this.settings.saveHistory && this.settings.scrollHistory) {
      this.addClickListenerProductCard();
      this.scrollToSavedPosition();
    }
  }

  // Parses an HTML string into a <template> fragment so nodes can be
  // cleaned up BEFORE they enter the live DOM (no flash of unstyled or
  // mid-animation content).
  //
  // - Duplicate <link rel="stylesheet"> tags that Shopify section renders
  //   embed at the top of each page are removed; they're already loaded.
  // - If stripAnimations is true, Dawn's scroll-trigger cascade classes are
  //   removed entirely so the content appears instantly. Used for content
  //   prepended above the viewport (back-nav restore), where a cascade
  //   would cause an opacity flash and fight the scroll-restoration math.
  //
  // NOTE (appended pages): the per-row stagger for appended content is
  // handled in Liquid, not here — the product grid template should set
  //   --animation-order: {{ forloop.index0 | modulo: section.settings.columns_desktop }}
  // so the order is row-relative and identical on every fetched page.
  prepareContent(html, stripAnimations) {
    const temp = document.createElement('template');
    temp.innerHTML = html;

    // Remove re-fetched stylesheet links (already present from page 1)
    temp.content.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      link.remove();
    });

    if (stripAnimations) {
      temp.content.querySelectorAll('.scroll-trigger').forEach((el) => {
        el.classList.remove('scroll-trigger', 'animate--slide-in');
        el.removeAttribute('data-cascade');
        el.style.removeProperty('--animation-order');
      });
    }

    return temp.content;
  }

  addScrollListeners() {
    if (!this.paginationElement) { return; }

    document.addEventListener('scroll', this.checkIfPaginationInView);
    window.addEventListener('resize', this.checkIfPaginationInView);
    window.addEventListener('orientationchange', this.checkIfPaginationInView);
  }

  addClickListener() {
    if (!this.paginationElement) { return; }

    this.nextPageLinkElement = this.paginationElement.querySelector('a');
    this.clickActive = true;

    if (typeof this.nextPageLinkElement !== 'undefined' && this.nextPageLinkElement !== null) {
      this.nextPageLinkElement.addEventListener('click', this.preventMultipleClicks);
    }
  }

  preventMultipleClicks(event) {
    event.preventDefault();

    if (!this.clickActive) { return; }

    this.nextPageLinkElement.innerText = this.settings.loadingText;
    this.nextPageUrl = this.nextPageLinkElement.href;
    this.clickActive = false;

    this.loadMore();
  }

  checkIfPaginationInView() {
    const top = this.paginationElement.getBoundingClientRect().top - this.settings.offset;
    const bottom = this.paginationElement.getBoundingClientRect().bottom + this.settings.offset;

    if (top <= window.innerHeight && bottom >= 0) {
      this.nextPageLinkElement = this.paginationElement.querySelector('a');
      this.removeScrollListener();

      if (this.nextPageLinkElement) {
        this.nextPageLinkElement.innerText = this.settings.loadingText;
        this.nextPageUrl = this.nextPageLinkElement.href;
        this.loadMore();
      }
    }
  }

  addClickListenerProductCard() {
    if (!this.containerElement) { return; }

    if (!this.containerElement.hasClickListener) {
      const productCardSelector = this.settings.productCard;

      const clickOrTouchHandler = function (event) {
        const productCard = event.target.closest(productCardSelector);

        if (productCard && this.contains(productCard)) {
          sessionStorage.setItem('scrollPosition', window.scrollY);
          sessionStorage.setItem('productId', productCard.id);
          sessionStorage.setItem('ajxContainerHeight', this.scrollHeight);
        }
      };

      this.containerElement.addEventListener('click', clickOrTouchHandler);
      this.containerElement.addEventListener('touchstart', clickOrTouchHandler, { passive: true });

      this.containerElement.hasClickListener = true;
    }
  }

  // Continuously saves scroll position so a page refresh can restore it.
  // Only saves when a ?page param is present (page 1 doesn't need restoring)
  // and never during loadPreviousContent itself (programmatic scrolls would
  // overwrite the value we're trying to restore).
  addScrollPositionSaver() {
    if (window.__ajxScrollSaverAdded) { return; }
    window.__ajxScrollSaverAdded = true;

    let timer;
    window.addEventListener('scroll', () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (this.isLoadingPreviousContent) { return; }
        if (!new URLSearchParams(window.location.search).get('page')) { return; }
        sessionStorage.setItem('ajxRefreshY', Math.round(window.scrollY));
        sessionStorage.setItem('ajxRefreshUrl', window.location.href);
        sessionStorage.setItem('ajxContainerHeight', this.containerElement.scrollHeight);
      }, 200);
    }, { passive: true });
  }

  async loadPreviousContent() {
    if (this.isLoadingPreviousContent) { return; }
    this.isLoadingPreviousContent = true;

    const urlParams = new URLSearchParams(window.location.search);
    const page = urlParams.get('page');

    if (page === null) {
      await this.scrollToSavedPosition();
      this.isLoadingPreviousContent = false;
      return;
    }

    this.removeScrollListener();

    // Insert a spacer above the current page's content whose height matches
    // the expected height of pages 1-(N-1). This means scrollTo(savedPosition)
    // immediately shows approximately the same content the user was looking at
    // when they clicked — rather than blank space (min-height) or the wrong
    // element (scrollIntoView using the current, pre-insertion offsetTop).
    // The spacer is swapped out for real content atomically after the XHRs
    // complete, so the user never sees a layout shift from the swap.
    const storedContainerHeight = Number(sessionStorage.getItem('ajxContainerHeight') || 0);
    const currentContainerHeight = this.containerElement.scrollHeight;
    const spacerHeight = storedContainerHeight > currentContainerHeight
      ? storedContainerHeight - currentContainerHeight
      : 0;

    let spacer = null;
    if (spacerHeight > 0) {
      spacer = document.createElement('div');
      spacer.id = '__ajx_spacer';
      spacer.style.cssText = `height:${spacerHeight}px;flex-shrink:0;`;
      this.containerElement.insertAdjacentElement('afterbegin', spacer);
    }
    sessionStorage.removeItem('ajxContainerHeight');

    // Now scroll to the exact saved position. The spacer makes the document
    // tall enough that this won't clip. Never use scrollIntoView here —
    // it recalculates from the current (wrong) offsetTop and creates a second
    // jump when the final scrollTo(savedPosition) fires after insertion.
    const peekY = Number(
      sessionStorage.getItem('scrollPosition') ||
      sessionStorage.getItem('ajxRefreshY') || 0
    );
    if (peekY) {
      window.scrollTo({ top: peekY, behavior: 'auto' });
    }

    const pageNum = parseInt(page, 10);
    const htmlContents = [];
    const promises = [];

    for (let i = pageNum - 1; i >= 1; i--) {
      const params = new URLSearchParams(window.location.search);
      params.set('page', i);
      const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;

      promises.push(new Promise((resolve) => {
        const request = new XMLHttpRequest();

        request.onreadystatechange = function () {
          if (request.readyState !== 4 || request.status !== 200) { return; }
          if (!request.responseXML) { resolve(); return; }

          const newContainer = request.responseXML.querySelectorAll(this.settings.container)[0];
          if (newContainer) {
            htmlContents[i] = newContainer.innerHTML;
          }
          resolve();
        }.bind(this);

        request.open('GET', url);
        request.responseType = 'document';
        request.send();
      }));
    }

    await Promise.all(promises);

    const combinedHTML = htmlContents
      .filter(content => content !== undefined)
      .join('');

    // Prepended content sits above the viewport — it must NOT run the
    // scroll-trigger cascade (would flash and re-animate already-seen items),
    // so strip animation classes before the nodes enter the DOM.
    const preparedContent = this.prepareContent(combinedHTML, true);

    // Swap the spacer for real content atomically while anchoring is suppressed.
    // Both operations happen synchronously before the browser reflows, so the
    // net height change is ~0 and scrollY doesn't move.
    document.documentElement.style.overflowAnchor = 'none';
    if (spacer) { spacer.style.display = 'none'; }   // remove spacer space
    this.containerElement.prepend(preparedContent);  // add real content
    if (spacer) { spacer.remove(); }                  // clean up spacer node
    document.documentElement.style.overflowAnchor = '';

    await this.scrollToSavedPosition();

    this.isLoadingPreviousContent = false;

    if (this.settings.method === 'scroll') {
      this.addScrollListeners();
    }
  }

  scrollToSavedPosition() {
    return new Promise((resolve) => {
      if (!this.settings.scrollHistory) { resolve(); return; }

      // Product click → back navigation
      const productId    = sessionStorage.getItem('productId');
      const savedPosition = sessionStorage.getItem('scrollPosition');

      // Refresh: continuous scroll position saved by addScrollPositionSaver
      const refreshY   = sessionStorage.getItem('ajxRefreshY');
      const refreshUrl = sessionStorage.getItem('ajxRefreshUrl');
      const isRefresh  = !productId && !savedPosition
                         && refreshUrl === window.location.href
                         && refreshY !== null;

      if (!productId && !savedPosition && !isRefresh) { resolve(); return; }

      // Consume all relevant keys upfront
      if (productId)    { sessionStorage.removeItem('productId'); }
      if (savedPosition) { sessionStorage.removeItem('scrollPosition'); }
      if (isRefresh)    {
        sessionStorage.removeItem('ajxRefreshY');
        sessionStorage.removeItem('ajxRefreshUrl');
      }

      const target  = productId ? document.getElementById(productId) : null;
      const scrollY = savedPosition ? Number(savedPosition)
                    : isRefresh     ? Number(refreshY)
                    : null;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollY !== null) {
            // Prefer the exact saved Y — it's pixel-accurate and the document
            // is already tall enough (min-height was applied before the XHRs).
            // scrollIntoView would introduce a small offset because it
            // recalculates center independently from where the user actually was.
            window.scrollTo({ top: scrollY, behavior: 'auto' });
          } else if (target) {
            // Fallback: no saved Y, center on the element
            target.scrollIntoView({ behavior: 'auto', block: 'center' });
          }
          resolve();
        });
      });
    });
  }

  loadMore() {
    this.request = new XMLHttpRequest();

    this.request.onreadystatechange = function success() {
      if (this.request.readyState !== 4 || this.request.status !== 200) { return; }
      if (!this.request.responseXML) { return; }

      const newContainer = this.request.responseXML.querySelectorAll(this.settings.container)[0];
      const newPagination = this.request.responseXML.querySelectorAll(this.settings.pagination)[0];

      // Appended content KEEPS its cascade animation classes — the per-row
      // stagger is correct because the Liquid template sets
      // --animation-order with `forloop.index0 | modulo: columns`, making
      // every fetched page identical to the first. We only dedupe the
      // <link> stylesheet tags each section render drags along.
      // (Pass `true` instead to make appended items appear instantly
      // with no animation.)
      const preparedContent = this.prepareContent(newContainer.innerHTML, false);
      this.containerElement.append(preparedContent);

      if (typeof newPagination === 'undefined') {
        window.history.pushState({ path: this.nextPageUrl }, '', this.nextPageUrl);
        this.removePaginationElement();
      } else {
        this.paginationElement.innerHTML = newPagination.innerHTML;
        window.history.pushState({ path: this.nextPageUrl }, '', this.nextPageUrl);

        if (this.settings.callback && typeof this.settings.callback === 'function') {
          this.settings.callback(this.request.responseXML);
        }

        this.initialize();
      }
    }.bind(this);

    this.request.open('GET', this.nextPageUrl);
    this.request.responseType = 'document';
    this.request.send();
  }

  removeClickListener() {
    this.nextPageLinkElement.removeEventListener('click', this.preventMultipleClicks);
  }

  removePaginationElement() {
    this.paginationElement.innerHTML = '';
    this.destroy();
  }

  removeScrollListener() {
    document.removeEventListener('scroll', this.checkIfPaginationInView);
    window.removeEventListener('resize', this.checkIfPaginationInView);
    window.removeEventListener('orientationchange', this.checkIfPaginationInView);
  }

  destroy() {
    const destroyers = {
      click: this.removeClickListener,
      scroll: this.removeScrollListener,
    };

    destroyers[this.settings.method]();

    // Restore default scroll restoration so other pages aren't affected
    if (this.settings.saveHistory) {
      history.scrollRestoration = 'auto';
    }

    return this;
  }
}